(() => {
  if (customElements.get('pe-cart-drawer')) return;

  const editable = ([key, value]) => !key.startsWith('_') &&
    key !== 'MANUAL REVIEW REQUIRED' && typeof value === 'string' &&
    !/(^|[ _-])(id|url|preview|artwork|svg|json|image|file|design)([ _-]|$)/i.test(key) &&
    !/^(https?:|data:|\/\/|\{|\[)/i.test(value.trim());

  class PeCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.dialog = this.querySelector('.pe-native-dialog');
      this.editor = this.querySelector('.pe-native-editor');
      this.root = this.dataset.rootUrl.replace(/\/?$/, '/');
      this.revision = 0;
      this.editorRevision = 0;
      this.disabledControls = new Map();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('error', event => this.restorePreview(event.target), { ...options, capture: true });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('pe-native-cart-open');
        if (!this.suppressRestore && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
        this.suppressRestore = false;
      }, options);
      this.editor.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); }, options);
      this.editor.addEventListener('close', () => {
        this.editorRevision++;
        if (this.dialog.open) {
          const line = this.querySelectorAll('[data-pe-line]')[this.editIndex];
          (line?.querySelector('[data-pe-native-edit]') || this.dialog.querySelector('[data-pe-native-close]')).focus({ preventScroll: true });
        }
      }, options);
      this.querySelector('[data-pe-native-apps]')?.addEventListener('click', event => {
        if (event.target.closest('button, a, [role="button"]')) this.close(false);
      }, { ...options, capture: true });
      window.addEventListener('pageshow', () => {
        this.resetCheckout();
        if (this.dialog.open && !this.busy) this.refresh().catch(() => this.message(this.dataset.error, true));
      }, options);
      this.fixPreviews();
      document.querySelectorAll('.header__icon--cart, [data-pe-native-open]').forEach(trigger => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
      });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.readController?.abort();
      clearTimeout(this.checkoutTimer);
      this.connected = false;
      document.documentElement.classList.remove('pe-native-cart-open');
    }

    open(trigger, refresh = true) {
      if (!this.dialog.open) {
        this.trigger = trigger || document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('pe-native-cart-open');
        this.dialog.querySelector('[data-pe-native-close]').focus({ preventScroll: true });
      }
      if (refresh && !this.busy) this.refresh().catch(() => this.message(this.dataset.error, true));
    }

    close(restore = true) {
      this.suppressRestore = !restore;
      this.dialog.close();
    }

    message(text = '', error = false) {
      const target = this.querySelector('[data-pe-native-status]');
      target.textContent = text;
      target.hidden = !text;
      target.classList.toggle('is-error', error);
      const recovery = this.querySelector('[data-pe-native-recovery]');
      if (recovery) recovery.hidden = !text || !error;
    }

    lockControls() {
      this.querySelectorAll('[data-pe-native-content] button, [data-pe-native-content] input, [data-pe-native-checkout], [data-pe-native-save], [data-pe-native-editor-cancel]').forEach(control => {
        if (!this.disabledControls.has(control)) this.disabledControls.set(control, control.disabled);
        control.disabled = true;
      });
    }

    setBusy(busy) {
      this.busy = busy;
      this.dialog.setAttribute('aria-busy', String(busy));
      const gift = this.querySelector('[data-pe-native-gift]');
      gift?.setLocked?.(busy || Boolean(this.checkingOut));
      if (busy) this.lockControls();
      else {
        this.disabledControls.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
        this.disabledControls.clear();
        if (this.upsellUncertain) this.querySelectorAll('[data-pe-native-upsell-add]').forEach(control => { control.disabled = true; });
      }
    }

    async json(url, options = {}) {
      // Storefront apps can wrap fetch and inspect its URL with string methods.
      // A URL object works with native fetch but can break those app wrappers.
      const response = await fetch(String(url), { credentials: 'same-origin', cache: 'no-store', ...options });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : this.dataset.error);
        error.rejected = true;
        error.status = Number(data.status) >= 400 ? Number(data.status) : response.status;
        throw error;
      }
      return data;
    }

    sections() { return [this.dataset.sectionId, 'cart-icon-bubble']; }

    async refresh() {
      if (this.checkingOut) return;
      const revision = ++this.revision;
      this.readController?.abort();
      this.readController = new AbortController();
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('sections', this.sections().join(','));
      try {
        const sections = await this.json(url, { signal: this.readController.signal });
        if (revision !== this.revision) return;
        this.render(sections);
        if (!this.busy) this.message();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[PE cart] Refresh failed:', error.message);
          throw error;
        }
      }
    }

    render(sections) {
      const html = sections?.[this.dataset.sectionId];
      if (typeof html !== 'string') throw new Error(this.dataset.error);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('pe-cart-drawer');
      const slots = ['[data-pe-native-content]', '[data-pe-native-footer]', '[data-pe-native-count]', '[data-pe-native-delivery]'];
      if (!replacement || slots.some(selector => !replacement.querySelector(selector))) throw new Error(this.dataset.error);
      this.dataset.checkoutLabel = replacement.dataset.checkoutLabel;
      const scroll = this.querySelector('.pe-native-scroll');
      const scrollTop = scroll.scrollTop;
      const focus = document.activeElement;
      const key = focus?.closest('[data-pe-line]')?.dataset.peLine;
      const index = key ? Array.from(this.querySelectorAll('[data-pe-line]')).findIndex(line => line.dataset.peLine === key) : -1;
      const controlName = focus?.hasAttribute('data-pe-native-quantity') ? '[data-pe-native-quantity]' : focus?.hasAttribute('data-pe-quantity-step') ? `[data-pe-quantity-step="${focus.dataset.peQuantityStep}"]` : null;
      const deliveryOpen = this.querySelector('.pe-native-delivery')?.open;
      slots.forEach(selector => {
        const target = this.querySelector(selector);
        const source = replacement.querySelector(selector);
        target.replaceChildren(...Array.from(source.childNodes));
        if (source.dataset.itemCount !== undefined) target.dataset.itemCount = source.dataset.itemCount;
      });
      this.querySelector('[data-pe-native-utilities]').hidden = Number(this.querySelector('[data-pe-native-content]').dataset.itemCount) === 0;
      const gift = this.querySelector('[data-pe-native-gift]');
      if (gift) {
        gift.hidden = Number(this.querySelector('[data-pe-native-content]').dataset.itemCount) === 0;
        const state = replacement.querySelector('[data-pe-gift-initial]');
        if (state) {
          try { gift.readAttributes?.(JSON.parse(state.textContent)); } catch {}
        }
      }
      if (deliveryOpen && this.querySelector('.pe-native-delivery')) this.querySelector('.pe-native-delivery').open = true;
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && typeof sections['cart-icon-bubble'] === 'string') {
        const bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const bubbleContent = bubbleDoc.getElementById('shopify-section-cart-icon-bubble') || bubbleDoc.body;
        bubble.replaceChildren(...Array.from(bubbleContent.childNodes));
      }
      if (this.busy) this.lockControls();
      scroll.scrollTop = scrollTop;
      this.fixPreviews();
      this.upsellUncertain = false;
      if (this.dialog.open && index >= 0) {
        requestAnimationFrame(() => {
          const line = this.querySelectorAll('[data-pe-line]')[index];
          const next = controlName && line?.querySelector(controlName);
          if (next && !next.disabled) next.focus({ preventScroll: true });
          else this.dialog.querySelector('[data-pe-native-close]').focus({ preventScroll: true });
        });
      }
      this.dispatchEvent(new CustomEvent('pe:cart:rendered', { bubbles: true }));
    }

    async receive(data) {
      this.revision++;
      this.readController?.abort();
      try { this.render(data.sections); }
      catch { await this.refresh(); }
    }

    restorePreview(image) {
      if (!(image instanceof HTMLImageElement) || !image.hasAttribute('data-pe-native-preview')) return;
      image.removeAttribute('data-pe-native-preview');
      image.src = image.dataset.fallbackSrc;
      const media = image.closest('.pe-native-item__media');
      media?.querySelector('[data-pe-native-sample]')?.removeAttribute('hidden');
      media?.querySelector('[data-pe-native-preview-label]')?.setAttribute('hidden', '');
    }

    fixPreviews() {
      this.querySelectorAll('[data-pe-native-preview]').forEach(image => { if (image.complete && !image.naturalWidth) this.restorePreview(image); });
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.hasAttribute('data-pe-native-close')) this.close();
      if (target.hasAttribute('data-pe-native-editor-cancel') && !this.busy) this.editor.close();
      if (target.hasAttribute('data-pe-native-edit') && !this.busy) this.edit(target);
      const line = target.closest('[data-pe-line]');
      if (!line || this.busy) return;
      if (target.hasAttribute('data-pe-native-remove')) this.change(line.dataset.peLine, 0);
      if (target.hasAttribute('data-pe-quantity-step')) {
        const input = line.querySelector('[data-pe-native-quantity]');
        const value = Number(input.value) + Number(target.dataset.peQuantityStep) * Number(input.step || 1);
        input.value = String(value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-pe-native-quantity]')) return;
      event.stopPropagation();
      if (this.busy) return;
      if (!input.checkValidity() || !Number.isSafeInteger(Number(input.value))) { input.reportValidity(); return; }
      this.change(input.closest('[data-pe-line]').dataset.peLine, Number(input.value));
    }

    async change(key, quantity) {
      if (this.busy || this.checkingOut) return;
      this.setBusy(true);
      this.message(this.dataset.loading);
      let sent = false;
      try {
        const cart = await this.json(this.root + 'cart.js');
        if (!cart.items.some(item => item.key === key)) throw new Error(this.dataset.stale);
        sent = true;
        const updated = await this.json(this.root + 'cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.sections(), sections_url: this.dataset.cartUrl })
        });
        await this.receive(updated);
        this.message(this.dataset.updated);
      } catch (error) {
        await this.refresh().catch(() => {});
        this.message(sent && !error.rejected ? this.dataset.uncertain : error.message, true);
      } finally { this.setBusy(false); }
    }

    async edit(button) {
      this.editTrigger = button;
      this.editIndex = Array.from(this.querySelectorAll('[data-pe-line]')).indexOf(button.closest('[data-pe-line]'));
      this.editOriginal = null;
      const revision = ++this.editorRevision;
      const fields = this.querySelector('[data-pe-native-fields]');
      const status = this.querySelector('[data-pe-native-editor-status]');
      const save = this.querySelector('[data-pe-native-save]');
      fields.replaceChildren();
      save.disabled = true;
      status.textContent = this.dataset.loading;
      this.editor.showModal();
      this.editor.focus({ preventScroll: true });
      try {
        const cart = await this.json(this.root + 'cart.js');
        if (revision !== this.editorRevision || !this.editor.open) return;
        const item = cart.items.find(line => line.key === button.dataset.peNativeEdit);
        if (!item) throw new Error(this.dataset.stale);
        const entries = Object.entries(item.properties || {}).filter(editable);
        if (!entries.length) throw new Error(this.dataset.emptyFields);
        let rules;
        try { rules = JSON.parse(button.dataset.peNativeRules); if (typeof rules === 'string') rules = JSON.parse(rules); } catch {}
        if (!Array.isArray(rules)) rules = [];
        entries.forEach(([name, value], index) => {
          const rule = rules.find(field => field.name === name || field.name?.replace(/ /g, '_') === name);
          const input = document.createElement(rule?.type === 'dropdown' ? 'select' : 'input');
          input.id = 'PeNativeField-' + index;
          input.dataset.property = name;
          if (input.tagName === 'SELECT') {
            const values = String(rule.value || '').split(',');
            if (!values.includes(value)) values.unshift(value);
            values.forEach(option => input.add(new Option(option, option)));
          } else {
            input.type = 'text';
            if (Number(rule?.length) > 0) input.maxLength = Number(rule.length);
            if (rule?.pattern) input.pattern = rule.pattern;
            if (rule?.pattern_message) input.title = rule.pattern_message;
          }
          input.required = rule?.required === true;
          input.value = value;
          const label = document.createElement('label');
          label.htmlFor = input.id;
          label.textContent = name.replace(/_/g, ' ');
          fields.append(label, input);
        });
        this.editOriginal = item;
        status.textContent = '';
        save.disabled = false;
      } catch (error) { if (revision === this.editorRevision) status.textContent = error.message; }
    }

    async save() {
      if (this.busy || !this.editOriginal) return;
      const form = this.querySelector('[data-pe-native-editor-form]');
      if (!form.reportValidity()) return;
      const edits = Object.fromEntries(Array.from(form.querySelectorAll('[data-property]'), input => [input.dataset.property, input.value]));
      if (Object.entries(edits).every(([key, value]) => this.editOriginal.properties[key] === value)) { this.editor.close(); return; }
      this.setBusy(true);
      const status = this.querySelector('[data-pe-native-editor-status]');
      status.textContent = this.dataset.loading;
      let sent = false;
      try {
        const cart = await this.json(this.root + 'cart.js');
        const item = cart.items.find(line => line.key === this.editOriginal.key);
        if (!item) throw new Error(this.dataset.stale);
        const properties = { ...item.properties, ...edits, _customer_edited: 'true', _edited_at: new Date().toISOString() };
        Object.keys(properties).filter(key => key.startsWith('_previewFile')).forEach(key => delete properties[key]);
        sent = true;
        const updated = await this.json(this.root + 'cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties, sections: this.sections(), sections_url: this.dataset.cartUrl })
        });
        if (!updated.items?.some(line => line.variant_id === item.variant_id && Object.entries(properties).every(([key, value]) => line.properties?.[key] === value))) throw new Error(this.dataset.uncertain);
        await this.receive(updated);
        this.editor.close();
        this.message(this.dataset.updated);
      } catch (error) {
        status.textContent = sent && !error.rejected ? this.dataset.uncertain : error.message;
        if (sent) { this.editOriginal = null; await this.refresh().catch(() => {}); }
      } finally {
        this.setBusy(false);
        if (!this.editOriginal) this.querySelector('[data-pe-native-save]').disabled = true;
      }
    }

    onSubmit(event) {
      if (event.target.matches('[data-pe-native-editor-form]')) {
        event.preventDefault();
        event.stopPropagation();
        this.save();
      }
      if (event.target.matches('[data-pe-native-checkout-form]')) {
        if (this.busy || this.checkingOut || !event.target.reportValidity()) { event.preventDefault(); return; }
        const gift = this.querySelector('[data-pe-native-gift]');
        if (gift?.beforeCheckout?.() === false) { event.preventDefault(); return; }
        this.checkingOut = true;
        gift?.setLocked?.(true);
        this.revision++;
        this.readController?.abort();
        const button = this.querySelector('[data-pe-native-checkout]');
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.querySelector('[data-pe-native-checkout-label]').textContent = this.dataset.opening;
        this.checkoutTimer = setTimeout(() => this.resetCheckout(), 15000);
      }
    }

    resetCheckout() {
      clearTimeout(this.checkoutTimer);
      this.checkingOut = false;
      const gift = this.querySelector('[data-pe-native-gift]');
      gift?.setLocked?.(Boolean(this.busy));
      const button = this.querySelector('[data-pe-native-checkout]');
      if (!button) return;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.querySelector('[data-pe-native-checkout-label]').textContent = this.dataset.checkoutLabel;
    }

    async add(form, submitter) {
      if (this.busy || this.checkingOut) return;
      const upsell = form.hasAttribute('data-pe-native-upsell-form');
      if (upsell && this.upsellUncertain) {
        this.message(this.dataset.uncertain, true);
        return;
      }
      if (!form.reportValidity()) return;
      if (submitter && Number(submitter.dataset.requiredFields || 0) > Number(submitter.dataset.validFields || 0)) {
        this.open(submitter, false);
        this.message(this.dataset.required, true);
        return;
      }
      const data = new FormData(form);
      if (submitter?.name) data.append(submitter.name, submitter.value);
      const allowed = form.dispatchEvent(new CustomEvent('pe:cart:before-add', { bubbles: true, cancelable: true, detail: { formData: data } }));
      if (!allowed) return;
      data.set('sections', this.sections().join(','));
      data.set('sections_url', this.dataset.cartUrl);
      const wasDisabled = submitter?.disabled;
      this.setBusy(true);
      if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); }
      if (!this.dialog.open) {
        const opener = upsell ? (this.trigger?.isConnected ? this.trigger : document.querySelector('.header__icon--cart, [data-pe-native-open]')) : submitter;
        this.open(opener, false);
      }
      this.message(this.dataset.loading);
      if (upsell) {
        this.revision++;
        this.readController?.abort();
      }
      let accepted = false, addedKey;
      try {
        const updated = await this.json(this.root + 'cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } });
        accepted = true;
        if (upsell) {
          const added = (Array.isArray(updated.items) ? updated.items : [updated]).find(item => String(item.variant_id || item.id) === String(data.get('id')));
          addedKey = added?.key;
        }
        await this.receive(updated);
        this.message(this.dataset.updated);
        document.querySelector('quick-add-modal[open]')?.hide?.(true);
      } catch (error) {
        const uncertain = !error.rejected || (upsell && (accepted || error.status >= 500 || error.status === 408));
        if (upsell && uncertain) this.upsellUncertain = true;
        await this.refresh().catch(() => {});
        this.message(uncertain ? this.dataset.uncertain : error.message, true);
      } finally {
        if (submitter) { submitter.disabled = wasDisabled; submitter.removeAttribute('aria-busy'); }
        this.setBusy(false);
        if (upsell && this.dialog.open) {
          const line = addedKey && Array.from(this.querySelectorAll('[data-pe-line]')).find(item => item.dataset.peLine === addedKey);
          const focus = line?.querySelector('[data-pe-native-quantity]') || (!accepted && submitter?.isConnected && !submitter.disabled ? submitter : null) || this.querySelector('[data-pe-native-checkout]') || this.dialog.querySelector('[data-pe-native-close]');
          focus?.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define('pe-cart-drawer', PeCartDrawer);

  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target.closest('.header__icon--cart, [data-pe-native-open]');
    const drawer = document.querySelector('pe-cart-drawer');
    if (!trigger || !drawer || trigger.hasAttribute('data-pe-native-full-cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    drawer.open(trigger);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    const drawer = document.querySelector('pe-cart-drawer');
    if (!drawer || !(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    const action = new URL(form.action, location.origin);
    if (action.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    if (form.hasAttribute('data-pe-native-bypass') || form.querySelector('[data-skip-cart="true"]') || event.submitter?.hasAttribute('data-skip-cart-button')) return;
    if (form.querySelector('.product-form__variants[data-values]:not([data-values=""])')) return;
    if (event.submitter?.disabled && !form.hasAttribute('data-pe-native-upsell-form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.submitter?.disabled) return;
    drawer.add(form, event.submitter);
  }, true);

  document.addEventListener('pe:cart:open', event => document.querySelector('pe-cart-drawer')?.open(event.detail?.trigger));
  for (const name of ['pe:cart:refresh', 'cart:refresh', 'cart:updated']) {
    document.addEventListener(name, () => {
      const drawer = document.querySelector('pe-cart-drawer');
      if (drawer?.dialog.open && !drawer.busy) drawer.refresh().catch(() => drawer.message(drawer.dataset.error, true));
    });
  }
})();

